import { useCallback, useEffect, useState } from "react";
import { getCacheStatuses, getCached, putCache, deleteCache } from "./pointcloudCache";
import { useProject } from "./ProjectContext";

export type ScanLocalStatus = "server_only" | "cached" | "downloading";

/**
 * Hook that tracks which pointcloud scans are cached locally in IndexedDB.
 * Provides download/delete actions and status per scan index.
 */
export function usePointcloudCacheStatus() {
  const project = useProject();
  const endpoints = project.pointcloudEndpoints;
  const [statuses, setStatuses] = useState<ScanLocalStatus[]>(() =>
    endpoints.map(() => "server_only")
  );
  const [progress, setProgress] = useState<Map<number, number>>(new Map());

  // Check cache on mount / project change
  useEffect(() => {
    let cancelled = false;
    const urls = endpoints.map((ep) => ep.url);
    getCacheStatuses(urls).then((map) => {
      if (cancelled) return;
      setStatuses(endpoints.map((ep) => (map.get(ep.url) ? "cached" : "server_only")));
    });
    return () => { cancelled = true; };
  }, [endpoints]);

  /** Download a scan to local IndexedDB cache */
  const downloadScan = useCallback(async (idx: number) => {
    const ep = endpoints[idx];
    if (!ep) return;
    setStatuses((prev) => { const n = [...prev]; n[idx] = "downloading"; return n; });
    setProgress((prev) => new Map(prev).set(idx, 0));
    try {
      const resp = await fetch(ep.url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      // Try to read with progress
      const contentLength = resp.headers.get("content-length");
      const total = contentLength ? parseInt(contentLength) : 0;

      if (total && resp.body) {
        const reader = resp.body.getReader();
        const chunks: Uint8Array[] = [];
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          setProgress((prev) => new Map(prev).set(idx, Math.round((received / total) * 100)));
        }
        const buf = new Uint8Array(received);
        let offset = 0;
        for (const chunk of chunks) { buf.set(chunk, offset); offset += chunk.length; }

        if (ep.format === "json") {
          const text = new TextDecoder().decode(buf);
          await putCache(ep.url, text);
        } else {
          await putCache(ep.url, buf.buffer);
        }
      } else {
        // Fallback: no content-length
        if (ep.format === "json") {
          const text = await resp.text();
          await putCache(ep.url, text);
        } else {
          const buf = await resp.arrayBuffer();
          await putCache(ep.url, buf);
        }
      }

      setStatuses((prev) => { const n = [...prev]; n[idx] = "cached"; return n; });
    } catch (e) {
      console.error(`Failed to cache scan ${ep.label}:`, e);
      setStatuses((prev) => { const n = [...prev]; n[idx] = "server_only"; return n; });
    }
    setProgress((prev) => { const n = new Map(prev); n.delete(idx); return n; });
  }, [endpoints]);

  /** Remove a scan from local cache */
  const removeScan = useCallback(async (idx: number) => {
    const ep = endpoints[idx];
    if (!ep) return;
    await deleteCache(ep.url);
    setStatuses((prev) => { const n = [...prev]; n[idx] = "server_only"; return n; });
  }, [endpoints]);

  /** Download all scans */
  const downloadAll = useCallback(async () => {
    for (let i = 0; i < endpoints.length; i++) {
      if (statuses[i] === "server_only") {
        await downloadScan(i);
      }
    }
  }, [endpoints, statuses, downloadScan]);

  return { statuses, progress, downloadScan, removeScan, downloadAll };
}

/**
 * Fetch scan data with cache-first strategy.
 * Returns ArrayBuffer for PLY or parsed JSON string for JSON format.
 */
export async function fetchWithCache(url: string, format: "json" | "ply"): Promise<ArrayBuffer | string> {
  const cached = await getCached(url);
  if (cached !== null) {
    return cached;
  }
  // Not cached — fetch from server
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  if (format === "json") {
    const text = await resp.text();
    await putCache(url, text);
    return text;
  } else {
    const buf = await resp.arrayBuffer();
    await putCache(url, buf);
    return buf;
  }
}
